import { t } from "i18next";
import { AppRadioButton } from "../../components/UI/AppRadioButtons/AppRadioButtons";
import {
  CompanyInvoiceFormFields,
  ReadOnlyIfInitialValuesNotProvided,
} from "../../components/Layouts/WebCheckInForms/FormOptions";
import { RegisterOptions } from "react-hook-form";
import { AppInputSwitcher } from "../../components/Layouts/AppInputSwitcher/AppInputSwitcher";
import { FormFieldErrorMessage } from "../../components/UI/FormFieldErrorMessage/FormFieldErrorMessage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWebCheckInForm } from "../../hooks/WebCheckInForm_2";
import {
  DefaultValuesDetailsForm,
  IndividualFormProps,
} from "../../components/Layouts/WebCheckInForms/WebCheckInForms.props";
import PrivatePersonalDetails from "./PrivatePersonalDetails";
import BookingGetters from "../../store/onlineBooking/getters";
import i18n from "../../config/i18n";
import { AppStandardMultiSelectCheckBox } from "../../components/UI/AppMultipleSelection";
import AppCountrySelect from "../../components/Layouts/AppCountrySelect";
import Container from "@mui/material/Container";
import { createFilterOptions } from "@mui/material";

function NewCompanyDetails(
  {
    userType,
    handleUserType,
    setCompanyinvoiceDetails,
    companyinvoiceDetails,
    phoneError,
    setHandleBillingHeight,
    phoneRef,
    dateError,
    isAirport,
    hideNumberOfFieldsAtPayAtDesk,
    paymentType,
    reservationSourcePayLater,
    reservationSourcePayNow,
    scriptChannel,
    privateValidateRef,
    companyValidateRef,
    customerDetails,
  }: any,
  {
    onSubmit = () => {},
    values = {},
    initialValues = { ...DefaultValuesDetailsForm },
    onChange = () => {},
  }: IndividualFormProps
) {
  const [
    {
      setValue,
      getValues,
      register,
      reset,
      trigger,
      setError,
      clearErrors,
      formState: { errors },
    },
    individualFormFields,
  ] = useWebCheckInForm(
    CompanyInvoiceFormFields,
    {
      ...Object.keys(values).reduce((prevValue: any, currentKey) => {
        prevValue[currentKey] =
          prevValue[currentKey] || (initialValues as any)[currentKey];
        return prevValue;
      }, values as any),
    },
    onChange
  );

  const { t: translate } = useTranslation();
  const [taxVal, setTaxVal] = useState("9999999999999999");
  const [isReadonly, setIsReadonly] = useState<boolean>(true);
  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;
  const [userId] = useState(localStorage.getItem("userID"));
  const [privatePersonalDetails, setPrivatePersonalDetails] = useState({
    ...DefaultValuesDetailsForm,
  });
  // Only pass customerDetails to PrivatePersonalDetails when the customer is Private.
  // For Company/Individual, keep it null so the child never spreads raw API fields into Redux.
  const [privateCustomerDetails, setPrivateCustomerDetails] =
    useState<any>(null);
  const AppSettings = BookingGetters.getAppSettings();
  const [companyCountryError, setCompanyCountryError] = useState<string>("");
  const [companyBirthNationError, setCompanyBirthNationError] =
    useState<string>("");
  const companyFormContainerRef = useRef<HTMLDivElement>(null);
  // Latest RHF errors — read inside rAF after trigger() to focus the first invalid field.
  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  const hasAttemptedSubmitRef = useRef<boolean>(false);

  const lang = i18n.language;
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const isSmallScreen = window.innerWidth <= 768;
  const data = textResource?.[lang]?.flatMap((item) => item.data || []) || [];

  const [requiredFieldLabel, setRequiredFieldLabel] = useState<string>("");
  const [companyDetailsLabel, setCompanyDetailsLabel] = useState<string>("");
  const [companyDetailsLabelRes, setCompanyDetailsLabelRes] =
    useState<string>("");
  const [userTypeManuallySelected, setUserTypeManuallySelected] =
    useState<boolean>(false);

  const hideNumberOfFieldsAtPayNow =
    AppSettings?.hideNumberOfFieldsAtPayNow ?? false;

  const [showBillingDetails, setShowBillingDetails] = useState<boolean>(false);

  const showOnePrice = AppSettings?.showOnePayMethodTwoPrice;
  const enableUppercaseInputField = AppSettings?.enableUppercaseInputField;
  const hideFieldsPayNowScriptChannel =
    paymentType === "payNow" &&
    reservationSourcePayNow == "false" &&
    reservationSourcePayLater == "false" &&
    scriptChannel &&
    hideNumberOfFieldsAtPayNow;
  const shouldShowBillingCheckbox = useMemo(() => {
    // if (showOnePrice) return false;
    return (
      (paymentType === "payNow" && hideNumberOfFieldsAtPayNow) ||
      (paymentType === "payLater" && hideNumberOfFieldsAtPayAtDesk) ||
      hideFieldsPayNowScriptChannel
    );
  }, [
    paymentType,
    hideNumberOfFieldsAtPayNow,
    hideNumberOfFieldsAtPayAtDesk,
    showOnePrice,
  ]);
  useEffect(() => {
    setUserTypeManuallySelected(false);
  }, [paymentType, shouldShowBillingCheckbox]);
  const shouldShowFullForm = !shouldShowBillingCheckbox || showBillingDetails;

  useEffect(() => {
    setUserTypeManuallySelected(false);
  }, [paymentType, shouldShowBillingCheckbox]);

  useEffect(() => {
    setShowBillingDetails(false);
    handleUserType("");
    reset(DefaultValuesDetailsForm);
    setPrivatePersonalDetails(DefaultValuesDetailsForm);
    setCompanyinvoiceDetails({});
    setTaxVal("9999999999999999");
    setIsReadonly(true);
  }, [paymentType, hideNumberOfFieldsAtPayNow, hideNumberOfFieldsAtPayAtDesk]);

  useEffect(() => {
    if (userTypeManuallySelected) return;

    if (!showOnePrice || showOnePrice) {
      if (shouldShowBillingCheckbox) {
        handleUserType("");
      }
      if (!shouldShowBillingCheckbox) {
        if (userType !== "Private") handleUserType("Private");
      }
    }

    if (shouldShowBillingCheckbox) {
      if (!showBillingDetails) {
        handleUserType("");
      }
      return;
    }

    if (!userType) handleUserType("Private");
  }, [
    paymentType,
    shouldShowBillingCheckbox,
    userType,
    showBillingDetails,
    showOnePrice,
  ]);

  useEffect(() => {
    const req = data.find((i) => i.key === "RequiredFieldLabel");
    const comp = data.find((i) => i.key === "companyDetailsLabel");
    const compRes = data.find((i) => i.key === "companyDetailsLabelRes");

    setRequiredFieldLabel(req?.body || translate("This is a required field"));
    setCompanyDetailsLabel(comp?.body || translate("company_details"));
    setCompanyDetailsLabelRes(compRes?.body || "Intestatario contratto");
  }, [data, translate]);

  const prefixFilterOptions = createFilterOptions({
    limit: 50,
    matchFrom: "start",
  });

  const handleCountryChange = (val: string): void => {
    setValue("country", val || "");
    setValue("city", "");
    setValue("state", "");
  };

  // const handleManualUserTypeChange = (val: string): void => {
  //   setUserTypeManuallySelected(true);
  //   handleUserType(val);
  // };
  const handleManualUserTypeChange = (val: string): void => {
    if (val === userType) return;
    setCompanyCountryError("");
    setCompanyBirthNationError("");
    setUserTypeManuallySelected(true);
    handleUserType(val);
  };
  useEffect(() => {
    setHandleBillingHeight((prev: boolean) => !prev);
  }, [showBillingDetails]);
  // useEffect(() => {
  //   if (!userTypeManuallySelected) return;
  //
  //   reset(DefaultValuesDetailsForm);
  // }, [userType]);
  const prevUserTypeRef = useRef<string>("");
  useEffect(() => {
    if (!userTypeManuallySelected) return;
    if (prevUserTypeRef.current === userType) return;
    prevUserTypeRef.current = userType;
    reset(DefaultValuesDetailsForm);
    setPrivatePersonalDetails(DefaultValuesDetailsForm);
    setCompanyinvoiceDetails({});
    setTaxVal("9999999999999999");
    setIsReadonly(true);
  }, [userType]);

  useEffect(() => {
    if (!companyValidateRef) return;
    companyValidateRef.current = async () => {
      hasAttemptedSubmitRef.current = true;

      if (userType !== "Company" && userType !== "Individual") return true;

      // Optional billing checkbox checked → billing fields are non-mandatory,
      if (showBillingDetails) {
        setCompanyCountryError("");
        setCompanyBirthNationError("");
        return true;
      }

      const shouldValidateFull =
        AppSettings?.showOnePayMethodTwoPrice ||
        !(
          (paymentType === "payNow" && hideNumberOfFieldsAtPayNow) ||
          (paymentType === "payLater" && hideNumberOfFieldsAtPayAtDesk)
        );
      if (!shouldValidateFull) {
        setCompanyCountryError("");
        setCompanyBirthNationError("");
        return true;
      }

      const resetDependents = (
        formKeys: string[],
        invoiceKeys: string[]
      ): void => {
        formKeys.forEach((k) => setValue(k, ""));
        setCompanyinvoiceDetails((prev: any) => {
          const next = { ...prev };
          invoiceKeys.forEach((k) => (next[k] = ""));
          return next;
        });
      };
      const focusField = (parentKey: string): void => {
        const input =
          companyFormContainerRef.current?.querySelector<HTMLInputElement>(
            `[data-field-key="${parentKey}"] input`
          );
        input?.scrollIntoView({ behavior: "smooth", block: "center" });
        input?.focus();
      };

      // custom error state), so compute their required-state here.
      const countryInvalid = !!(
        AppSettings?.countryRequired && !getValues("country")
      );
      const birthNationInvalid = !!(
        userType === "Individual" &&
        AppSettings?.birthNationRequired &&
        !getValues("birthNation")
      );
      if (countryInvalid) {
        setCompanyCountryError(
          `${translate("Country")} ${translate("is required")}`
        );
      } else {
        setCompanyCountryError("");
      }
      if (birthNationInvalid) {
        setCompanyBirthNationError(
          `${translate("Birth Nation")} ${translate("is required")}`
        );
      } else {
        setCompanyBirthNationError("");
      }

      // Validate ALL RHF-registered fields at once so every invalid field shows
      const rhfValid = await trigger();
      const valid = rhfValid && !countryInvalid && !birthNationInvalid;

      if (!valid) {
        // country/birthNation and the RHF-registered fields as one ordered list.
        const isFieldInvalid = (key: string): boolean => {
          if (key === "country") return countryInvalid;
          if (key === "birthNation") return birthNationInvalid;
          return !!errorsRef.current?.[key];
        };
        requestAnimationFrame(() => {
          const firstError: any = Object.values(individualFormFields).find(
            (f: any) => f?.key && isFieldInvalid(f.key)
          );
          if (firstError) focusField(firstError.key);
        });
      }
      return valid;
    };
  }, [
    companyValidateRef,
    userType,
    getValues,
    AppSettings,
    paymentType,
    hideNumberOfFieldsAtPayNow,
    hideNumberOfFieldsAtPayAtDesk,
    translate,
    trigger,
    showBillingDetails,
  ]);

  const isGretaBookingNewUi = AppSettings?.isGretaBookingNewUi;

  const normalizeCustomerType = (type: string): string => {
    if (!type) return "Private";
    const map: Record<string, string> = {
      INDIVIDUAL: "Private",
      PRIVATE: "Private",
      COMPANY: "Company",
      INDIVIDUAL_WITH_VAT: "Individual",
    };
    return map[type.toUpperCase()] ?? "Private";
  };

  const mapCustomerToFormFields = (
    customer: any,
    type?: string
  ): Record<string, any> => {
    const isPrivate = type === "Private";
    const sex =
      customer.sex === "MALE" ||
      customer.sex === "MASCHIO" ||
      customer.sex === "MASCULINO"
        ? "MASCHIO"
        : customer.sex === "FEMALE" ||
          customer.sex === "FEMMINA" ||
          customer.sex === "FEMININA"
        ? "FEMMINA"
        : customer.sex || "";
    return {
      // Common fields (form registration keys)
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      email: customer.email || "",
      telephone: customer.mobileNumber || customer.telephone || "",
      streetName: customer.streetName || "",
      streetNumber: customer.streetNumber || "",
      zip: customer.zip || customer.zipCode || "",
      city: customer.city || "",
      country: customer.country || "",
      state: customer.state || customer.province || "",
      sex,
      // Company / Individual form keys (excluded for Private — API rejects them in customerInfo)
      ...(!isPrivate && {
        CompanyName: customer.companyName || "",
        CompanyPhoneNumber: customer.mobileNumber || customer.telephone || "",
        CompanyEmail: customer.email || "",
        companyName: customer.companyName || "", // key: "companyName"
        vatNumber: customer.vatNumber || customer.vat || "", // key: "vatNumber"
        CompanyEInvoicingEmail: customer.CompanyEInvoicingEmail || "",
        CompanyEInvoicingCode: customer.CompanyEInvoicingCode || "",
        CompanySSNNumber: customer.ssnNumber || "", // key: "CompanySSNNumber"
      }),
      // Private-only form keys (kept for both so reset doesn't blank private form when type is Private)
      taxCode: customer.taxCode || customer.fiscalCode || "",
      ssnNumber: customer.ssnNumber || "",
      birthNation: customer.birthNation || "",
      birthPlace: customer.birthPlace || "",
      birthProvince: customer.birthProvince || "",
      dateOfBirth: customer.dateOfBirth || "",
    };
  };

  // Maps customer API data to the keys BookingApi reads from CompanyInfo (companyinvoiceDetails).
  // Uses the outer CompanyInvoiceFormFields keys, e.g. CompanyName, CompanyPhoneNumber, CompanyStreet.
  const mapCustomerToCompanyInfo = (customer: any): Record<string, any> => {
    const sex =
      customer.sex === "MALE" ||
      customer.sex === "MASCHIO" ||
      customer.sex === "MASCULINO"
        ? "MASCHIO"
        : customer.sex === "FEMALE" ||
          customer.sex === "FEMMINA" ||
          customer.sex === "FEMININA"
        ? "FEMMINA"
        : customer.sex || "";
    return {
      CompanyName: customer.companyName || "",
      CompanyPhoneNumber: customer.mobileNumber || customer.telephone || "",
      CompanyEmail: customer.email || "",
      CompanyVatNumber: customer.vatNumber || customer.vat || "",
      CompanyStreet: customer.streetName || "",
      CompanyStreetNumber: customer.streetNumber || "",
      CompanyPostalCode: customer.zip || customer.zipCode || "",
      CompanyBirthProv: customer.birthProvince || "",
      CompanyEInvoicingEmail: customer.CompanyEInvoicingEmail || "",
      CompanyEInvoicingCode: customer.CompanyEInvoicingCode || "",
      CompanySsn: customer.ssnNumber || "",
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      country: customer.country || "",
      city: customer.city || "",
      state: customer.state || customer.province || "",
      birthDate: customer.dateOfBirth || "",
      birthNation: customer.birthNation || "",
      birthPlace: customer.birthPlace || "",
      gender: sex,
    };
  };

  useEffect(() => {
    if (!customerDetails) return;

    const resolvedType = normalizeCustomerType(customerDetails.customerType);
    const mappedFields = mapCustomerToFormFields(customerDetails, resolvedType);

    // Lock the type so the auto-set-to-Private effect doesn't override our detected type
    setUserTypeManuallySelected(true);

    if (resolvedType === "Private") {
      setPrivateCustomerDetails(customerDetails);
      setPrivatePersonalDetails((prev) => ({ ...prev, ...mappedFields }));
      handleUserType("Private");
    } else {
      const companyInfo = mapCustomerToCompanyInfo(customerDetails);
      setPrivateCustomerDetails(null);
      setPrivatePersonalDetails({ ...DefaultValuesDetailsForm });
      reset({ ...DefaultValuesDetailsForm, ...mappedFields });
      setCompanyinvoiceDetails((prev: any) => ({ ...prev, ...companyInfo }));
      handleUserType(resolvedType);
      setTimeout(() => {
        setValue("sex", mappedFields.sex);
        setValue("streetName", mappedFields.streetName);
        setValue("streetNumber", mappedFields.streetNumber);
        setValue("companyName", mappedFields.companyName);
        setValue("vatNumber", mappedFields.vatNumber);
        setValue("telephone", mappedFields.telephone);
        setValue("email", mappedFields.email);
      }, 100);
    }
  }, [customerDetails]);

  useEffect(() => {
    if (!userTypeManuallySelected) return;
    if (!customerDetails) {
      reset(DefaultValuesDetailsForm);
      setPrivatePersonalDetails({ ...DefaultValuesDetailsForm });
      return;
    }

    const mappedFields = mapCustomerToFormFields(customerDetails, userType);

    if (userType === "Private") {
      reset(DefaultValuesDetailsForm);
      setCompanyinvoiceDetails({});
      setPrivatePersonalDetails((prev) => ({ ...prev, ...mappedFields }));
    } else {
      const companyInfo = mapCustomerToCompanyInfo(customerDetails);
      setPrivatePersonalDetails({ ...DefaultValuesDetailsForm });
      reset({ ...DefaultValuesDetailsForm, ...mappedFields });
      setCompanyinvoiceDetails((prev: any) => ({ ...prev, ...companyInfo }));
      setTimeout(() => {
        setValue("streetName", mappedFields.streetName);
        setValue("streetNumber", mappedFields.streetNumber);
        setValue("sex", mappedFields.sex);
        setValue("companyName", mappedFields.companyName);
        setValue("vatNumber", mappedFields.vatNumber);
        setValue("telephone", mappedFields.telephone);
        setValue("email", mappedFields.email);
      }, 150);
    }
  }, [userType]);

  return (
    <div
      className={`box-border ${
        isGretaBookingNewUi ? "pl-10" : isSmallScreen ? "p-2" : "p-8"
      } ${isGretaBookingNewUi ? "bg-[#f5f5f5] w-full md:w-[80%]" : "bg-white"}`}
    >
      {shouldShowBillingCheckbox && (
        <AppStandardMultiSelectCheckBox
          key={paymentType + (showBillingDetails ? "1" : "0")}
          required={false}
          color="primary"
          value={showBillingDetails ? ["showBilling"] : []}
          options={[
            { label: t("Enter Billing Details"), value: "showBilling" },
          ]}
          onChange={(vals) => {
            const checked = vals.includes("showBilling");
            setShowBillingDetails(checked);
            if (!checked) handleUserType("");
          }}
          className="py-2 mb-5"
        />
      )}

      {shouldShowFullForm && (
        <>
          <h2
            className="text-xl font-bold text-black pb-8"
            style={{
              paddingLeft: isSmallScreen ? ".7rem" : "",
              paddingTop: isSmallScreen ? "2rem" : "",
              marginTop: showBillingDetails ? "25px" : "'",
            }}
          >
            {userId ? companyDetailsLabelRes : companyDetailsLabel}
          </h2>

          <div className={`flex flex-col ${isSmallScreen ? "px-2" : ""}`}>
            <AppRadioButton
              required={true}
              value={userType}
              onChange={handleManualUserTypeChange}
              options={[
                { label: t("Private"), value: "Private" },
                { label: t("Company"), value: "Company" },
                { label: t("Individual"), value: "Individual" },
              ]}
              color="primary"
            />

            <div style={{ display: userType === "Private" ? "block" : "none" }}>
              <PrivatePersonalDetails
                userType={userType}
                userDetails={privatePersonalDetails}
                setUserDetails={setPrivatePersonalDetails}
                dateError={dateError}
                isAirport={isAirport}
                hideNumberOfFieldsAtPayAtDesk={hideNumberOfFieldsAtPayAtDesk}
                hideNumberOfFieldsAtPayNow={hideNumberOfFieldsAtPayNow}
                paymentType={paymentType}
                privateValidateRef={privateValidateRef}
                isBillingOptional={showBillingDetails}
                customerDetails={privateCustomerDetails}
              />
            </div>

            {userType && userType !== "Private" ? (
              <div
                ref={companyFormContainerRef}
                className={
                  isGretaBookingNewUi
                    ? "flex flex-col pt-4 pb-4 pr-4 gap-3 w-full md:w-[85%]"
                    : `grid sm:grid-cols-2 ${
                        istabletScreen ? "md:grid-cols-2" : "md:grid-cols-3"
                      } gap-4`
                }
                style={
                  isGretaBookingNewUi
                    ? undefined
                    : { padding: isSmallScreen ? "0px" : "2rem" }
                }
              >
                {Object.values(individualFormFields).map((el: any, index) => {
                  let taxValue = "";
                  let readOnly;
                  const telephoneValue =
                    el.key === "telephone"
                      ? getValues(el.key) ||
                        (initialValues as any)[el.key] ||
                        values[el.key] ||
                        ""
                      : "";
                  el.readOnly ||
                    (ReadOnlyIfInitialValuesNotProvided.includes(el.key) &&
                      !!(initialValues as any)[el.key]);
                  if (el.key === "taxCode") {
                    taxValue = taxVal;
                    readOnly = isReadonly;
                  } else {
                    taxValue = "";
                  }

                  const shouldValidate = !(
                    hideNumberOfFieldsAtPayAtDesk && paymentType === "payLater"
                  );

                  // Single source of truth — mirrors the AppSettings flags in the UI
                  const isFieldRequired: boolean = (() => {
                    // Optional billing checkbox checked → no billing field is
                    // mandatory, regardless of AppSettings (mirrors Private form).
                    if (showBillingDetails) return false;
                    switch (el.key) {
                      case "firstName":
                        return !!(
                          AppSettings?.firstNameRequired && shouldValidate
                        );
                      case "lastName":
                        return !!(
                          AppSettings?.lastNameRequired && shouldValidate
                        );
                      case "companyName":
                        return !!(
                          AppSettings?.companyNameRequired && shouldValidate
                        );
                      case "telephone":
                        return !!(
                          telephoneValue.length < 5 &&
                          AppSettings?.phoneNoRequired &&
                          shouldValidate
                        );
                      case "email":
                        return !!(AppSettings?.emailRequired && shouldValidate);
                      case "city":
                        return !!(AppSettings?.cityRequired && shouldValidate);
                      case "dateOfBirth":
                        return !!(
                          AppSettings?.birthDateRequired && shouldValidate
                        );
                      case "birthPlace":
                        return !!(
                          AppSettings?.birthPlaceRequired && shouldValidate
                        );
                      case "streetName":
                        return !!(
                          AppSettings?.streetNameRequired && shouldValidate
                        );
                      case "streetNumber":
                        return !!(AppSettings?.nRequired && shouldValidate);
                      case "birthProvince":
                        return !!(
                          AppSettings?.birthProvRequired && shouldValidate
                        );
                      case "CompanyEInvoicingEmail":
                        return !!(
                          AppSettings?.sdiEmailRequired && shouldValidate
                        );
                      case "CompanyEInvoicingCode":
                        return !!(
                          AppSettings?.sdiCodeRequired && shouldValidate
                        );
                      case "zip":
                        return !!(
                          AppSettings?.zipCodeRequired && shouldValidate
                        );
                      case "vatNumber":
                        return true;
                      case "sex":
                        return !!(
                          AppSettings?.genderRequired && shouldValidate
                        );
                      case "CompanySSNNumber":
                        return !!(
                          AppSettings?.ssnRequired &&
                          (AppSettings?.showOnePayMethodTwoPrice ||
                            shouldValidate)
                        );
                      default:
                        return false;
                    }
                  })();

                  const options: RegisterOptions = {
                    required: {
                      value: isFieldRequired,
                      message: `${translate(el.label || el.key)} ${translate(
                        "is required"
                      )}`,
                    },
                  };
                  if (el.type === "email") {
                    options.pattern = {
                      value: /^[a-zA-Z\d+_.-]+@[a-zA-Z\d.-]+$/,
                      message: translate("Please provide a valid email"),
                    };
                  }
                  if (el.key === "vatNumber") {
                    options.validate = (value): string | boolean => {
                      const birthNation = getValues("birthNation");
                      if (birthNation === "ITALIA" || userType === "Company") {
                        if (!/^\d+$/.test(value))
                          return String(
                            t("VAT number must contain only digits")
                          );
                        if (value.length !== 11)
                          return String(t("VAT number must be 11 digits long"));
                      }
                      return true;
                    };
                  }

                  // Mandatory state for the Country / Birth Nation dropdowns —
                  // reused for the required prop and the on-blur dropdown check.
                  const isDropdownRequired =
                    !showBillingDetails &&
                    ((el.key === "country" &&
                      AppSettings?.countryRequired &&
                      (AppSettings?.showOnePayMethodTwoPrice ||
                        !(
                          (paymentType === "payNow" &&
                            hideNumberOfFieldsAtPayNow) ||
                          (paymentType === "payLater" &&
                            hideNumberOfFieldsAtPayAtDesk)
                        ))) ||
                      (el.key === "birthNation" &&
                        AppSettings?.birthNationRequired &&
                        (AppSettings?.showOnePayMethodTwoPrice ||
                          !(
                            (paymentType === "payNow" &&
                              hideNumberOfFieldsAtPayNow) ||
                            (paymentType === "payLater" &&
                              hideNumberOfFieldsAtPayAtDesk)
                          ))));

                  return (
                    <React.Fragment key={index}>
                      {userType === "Company" &&
                      (el.key === "birthNation" ||
                        el.key === "birthPlace" ||
                        el.key === "dateOfBirth" ||
                        el.key === "birthProvince" ||
                        el.key === "taxCode" ||
                        el.key === "firstName" ||
                        el.key === "lastName" ||
                        el.key === "CompanySSNNumber" ||
                        el.key === "sex") ? (
                        ""
                      ) : (
                        <div
                          // key={index}
                          data-field-key={el.key}
                          className={
                            isGretaBookingNewUi
                              ? el.key === "country"
                                ? "relative z-50"
                                : el.key === "birthNation"
                                ? "relative z-40"
                                : el.key === "telephone"
                                ? "relative z-[60]"
                                : "relative z-0"
                              : ""
                          }
                          style={{
                            paddingBottom:
                              isSmallScreen && el.key === "sex"
                                ? "20px"
                                : "0px",
                          }}
                        >
                          {/*  CUSTOM COUNTRY SELECT */}
                          {el.key === "country" || el.key === "birthNation" ? (
                            <>
                              <div
                                className={
                                  isGretaBookingNewUi
                                    ? "relative z-20 w-full box-border bg-white"
                                    : "country-section"
                                }
                              >
                                <AppCountrySelect
                                  fieldKey={el.key}
                                  label={el.label}
                                  options={el.options || []}
                                  value={
                                    getValues(el.key) ||
                                    (initialValues as any)[el.key] ||
                                    values[el.key] ||
                                    ""
                                  }
                                  readOnly={readOnly}
                                  required={isDropdownRequired}
                                  translate={translate}
                                  formStates={{ setValue }}
                                  setUserDetails={(updated: any) => {
                                    setCompanyinvoiceDetails(updated);
                                  }}
                                  onChange={(val: string) => {
                                    setValue(el.key, val);
                                    if (el.key === "country") {
                                      setCompanyCountryError("");
                                    } else if (el.key === "birthNation") {
                                      setCompanyBirthNationError("");
                                    }
                                    if (val && hasAttemptedSubmitRef.current) {
                                      const deps =
                                        el.key === "country"
                                          ? ["city", "state", "zip"]
                                          : ["birthPlace", "birthProvince"];
                                      trigger(deps);
                                    }
                                  }}
                                  onSelectionValidityChange={(
                                    valid: boolean
                                  ) => {
                                    // A typed value that wasn't picked from the
                                    // "is required" message is never clobbered.
                                    const dropdownMsg = translate(
                                      "Please select a value from the dropdown"
                                    );
                                    const setErr =
                                      el.key === "country"
                                        ? setCompanyCountryError
                                        : el.key === "birthNation"
                                        ? setCompanyBirthNationError
                                        : null;
                                    if (!setErr) return;
                                    if (!valid) {
                                      setErr(dropdownMsg);
                                    } else {
                                      setErr((prev) =>
                                        prev === dropdownMsg ? "" : prev
                                      );
                                    }
                                  }}
                                  cityFieldName={
                                    el.key === "country" ? "city" : "birthPlace"
                                  }
                                  stateFieldName={
                                    el.key === "country"
                                      ? "state"
                                      : "birthProvince"
                                  }
                                  zipFieldName={
                                    el.key === "country" ? "zip" : undefined
                                  }
                                  enableUppercaseInputField={
                                    enableUppercaseInputField
                                  }
                                />
                              </div>
                              {/* Error message → BELOW the field (same as others). */}
                              {el.key === "country" && companyCountryError && (
                                <p
                                  className={`text-red-500 text-sm mt-1 ${
                                    isGretaBookingNewUi ? "" : "md:ml-[30px]"
                                  }`}
                                >
                                  {companyCountryError}
                                </p>
                              )}
                              {el.key === "birthNation" &&
                                companyBirthNationError && (
                                  <p
                                    className={`text-red-500 text-sm mt-1 ${
                                      isGretaBookingNewUi ? "" : "md:ml-[30px]"
                                    }`}
                                  >
                                    {companyBirthNationError}
                                  </p>
                                )}
                              {isGretaBookingNewUi
                                ? errors[el.key]?.message && (
                                    <p className="text-red-500 text-sm mt-1">
                                      {errors[el.key]?.message}
                                    </p>
                                  )
                                : errors[el.key]?.message && (
                                    <FormFieldErrorMessage className="md:ml-[30px]">
                                      {errors[el.key]?.message}
                                    </FormFieldErrorMessage>
                                  )}
                            </>
                          ) : (
                            /* ORIGINAL INPUT SWITCHER */
                            <>
                              <AppInputSwitcher
                                {...el}
                                key={index}
                                autoComplete="off"
                                enableUppercaseInputField={
                                  enableUppercaseInputField
                                }
                                inputRef={
                                  el.key === "telephone" &&
                                  phoneRef &&
                                  AppSettings?.phoneNoRequired
                                }
                                required={isFieldRequired}
                                {...register(el.key, { ...options })}
                                readOnly={readOnly}
                                value={
                                  getValues(el.key) ||
                                  (initialValues as any)[el.key] ||
                                  values[el.key] ||
                                  taxValue ||
                                  ""
                                }
                                options={
                                  el.key === "sex"
                                    ? [translate("MALE"), translate("FEMALE")]
                                    : el.options
                                }
                                {...((el.key === "city" ||
                                  el.key === "birthPlace") && {
                                  filterOptions: prefixFilterOptions,
                                })}
                                {...([
                                  "city",
                                  "state",
                                  "zip",
                                  "birthPlace",
                                  "birthProvince",
                                ].includes(el.key) &&
                                  el.type === "select" && {
                                    onSelectionValidityChange: (
                                      valid: boolean
                                    ) => {
                                      const dropdownMsg = translate(
                                        "Please select a value from the dropdown"
                                      );
                                      if (!valid) {
                                        setError(el.key, {
                                          type: "dropdown",
                                          message: dropdownMsg,
                                        });
                                      } else if (
                                        errors[el.key]?.type === "dropdown"
                                      ) {
                                        clearErrors(el.key);
                                      }
                                    },
                                  })}
                                label={translate(el.label || "")}
                                placeholder={translate(el.placeholder || "")}
                                className={
                                  isGretaBookingNewUi
                                    ? el.key === "sex"
                                      ? "w-full box-border mt-4 mb-4"
                                      : "w-full box-border bg-white"
                                    : `h-20 box-border ${
                                        !isSmallScreen ? "px-8" : ""
                                      }`
                                }
                                onchange={(e: any) => {
                                  const r = Object.keys(
                                    CompanyInvoiceFormFields
                                  ).at(index);
                                  const processedValue =
                                    enableUppercaseInputField &&
                                    typeof e === "string"
                                      ? e.toUpperCase()
                                      : e;

                                  if (
                                    el.key === "birthNation" &&
                                    processedValue === "ITALIA"
                                  ) {
                                    setTaxVal("");
                                    setIsReadonly(false);
                                  } else if (
                                    el.key === "birthNation" &&
                                    processedValue !== "ITALIA"
                                  ) {
                                    setTaxVal("9999999999999999");
                                    setIsReadonly(true);
                                  }

                                  setCompanyinvoiceDetails({
                                    ...companyinvoiceDetails,
                                    [r]: processedValue,
                                  });

                                  if (el.key === "country") {
                                    handleCountryChange(processedValue);
                                  }

                                  setValue(el.key, processedValue || "");
                                  // doesn't auto-clear the error while typing.
                                  if (processedValue) {
                                    clearErrors(el.key);
                                  }
                                }}
                              />
                              {isGretaBookingNewUi ? (
                                errors[el.key]?.message && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors[el.key]?.message}
                                  </p>
                                )
                              ) : (
                                <FormFieldErrorMessage
                                  className={!isSmallScreen ? "px-8" : ""}
                                >
                                  {errors[el.key]?.message}
                                </FormFieldErrorMessage>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export default NewCompanyDetails;
